/**
 * Word often puts a real grid in `text/rtf` (`\trowd`) and only loose
 * paragraphs in `text/html`. When sanitizing that HTML leaves no table, and
 * the visible text is exactly the RTF cells, rebuild the grid.
 */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function decodeRtfFragment(fragment: string): string {
  return fragment
    .replace(/\\'([0-9a-fA-F]{2})/g, (_, hex: string) =>
      String.fromCharCode(Number.parseInt(hex, 16)),
    )
    .replace(/\\u(-?\d+)\??/g, (_, code: string) => {
      const parsed = Number(code);
      const codePoint = parsed < 0 ? parsed + 65536 : parsed;
      return String.fromCharCode(codePoint);
    })
    .replace(/\\par\b|\\line\b/g, ' ')
    .replace(/\\tab\b/g, ' ')
    .replace(/\\[a-zA-Z]+-?\d* ?/g, '')
    .replace(/\\([{}\\])/g, '$1')
    .replace(/[{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

type RtfCell = {
  text: string;
  hContinue: boolean;
  vContinue: boolean;
};

function rowCells(rowBody: string): RtfCell[] {
  const parts = rowBody.split(/\\cellx-?\d+/);
  const defs = parts.length > 1 ? parts.slice(0, -1) : [];
  const content = parts.length > 1 ? parts[parts.length - 1] ?? '' : rowBody;
  const pieces = content.split(/\\cell\b/);
  if (pieces.length > 1) {
    pieces.pop();
  }
  return pieces.map((piece, index) => {
    const def = defs.at(index) ?? '';
    return {
      text: decodeRtfFragment(piece),
      hContinue: /\\clmrg\b/.test(def) && !/\\clmgf\b/.test(def),
      vContinue: /\\clvmrg\b/.test(def) && !/\\clvmgf\b/.test(def),
    };
  });
}

type HtmlCell = {
  text: string;
  colspan: number;
  rowspan: number;
};

/** One HTML table, or null when the RTF has no multi-cell grid. */
export function rtfTablesToHtml(rtf: string): string | null {
  if (!rtf.includes('\\trowd')) {
    return null;
  }
  const parsedRows: RtfCell[][] = [];
  const rowPattern = /\\trowd\b([\s\S]*?)\\row\b/g;
  let match = rowPattern.exec(rtf);
  while (match) {
    const cells = rowCells(match[1]);
    if (cells.length >= 2 && cells.some(cell => cell.text.length > 0)) {
      parsedRows.push(cells);
    }
    match = rowPattern.exec(rtf);
  }
  if (parsedRows.length < 2) {
    return null;
  }

  const grid: HtmlCell[][] = parsedRows.map(row => {
    const htmlRow: HtmlCell[] = [];
    for (const cell of row) {
      const previous = htmlRow.at(-1);
      if (cell.hContinue && cell.text.length === 0 && previous) {
        previous.colspan += 1;
        continue;
      }
      htmlRow.push({ text: cell.text, colspan: 1, rowspan: 1 });
    }
    return htmlRow;
  });

  parsedRows.forEach((parsedRow, rowIndex) => {
    let column = 0;
    for (const cell of parsedRow) {
      if (cell.vContinue && cell.text.length === 0) {
        for (let above = rowIndex - 1; above >= 0; above -= 1) {
          const target = grid.at(above)?.at(column);
          if (target) {
            target.rowspan += 1;
            break;
          }
        }
        grid.at(rowIndex)?.splice(column, 1, {
          text: '',
          colspan: 1,
          rowspan: 0,
        });
      }
      column += 1;
    }
  });

  const body = grid
    .map(row => {
      const cells = row
        .filter(cell => cell.rowspan > 0)
        .map(cell => {
          const colspan = cell.colspan > 1 ? ` colspan="${cell.colspan}"` : '';
          const rowspan = cell.rowspan > 1 ? ` rowspan="${cell.rowspan}"` : '';
          return `<td${colspan}${rowspan}><p>${escapeHtml(cell.text)}</p></td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  return `<table data-ss-grid="1"><tbody>${body}</tbody></table>`;
}

function visiblePasteText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Replace a flattened Word/Excel paste with the RTF grid when that grid is
 * the whole paste. Leaves email HTML that already has a table, and leaves
 * pastes whose text is more than the grid.
 */
export function recoverWordTablePaste(input: {
  sanitizedHtml: string;
  rtf: string;
}): string | null {
  if (/<table\b/i.test(input.sanitizedHtml)) {
    return null;
  }
  const tableHtml = rtfTablesToHtml(input.rtf);
  if (!tableHtml) {
    return null;
  }
  if (visiblePasteText(input.sanitizedHtml) !== visiblePasteText(tableHtml)) {
    return null;
  }
  return tableHtml;
}
